import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import AppShell from './AppShell';
import { API_BASE_URL } from '../config/api';
import { isWeChatEmbeddedBrowser } from '../utils/wechat';
import { getSignInReturnTo } from '../utils/signInReturn';
import { ChevronLeft } from 'lucide-react';
import './Login.css';
import './LoginMethod.css';

const LoginMethod: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [configured, setConfigured] = useState<boolean | null>(null);
  const [showQrModal, setShowQrModal] = useState(false);
  const [qrUrl, setQrUrl] = useState<string | null>(null);
  const [qrErr, setQrErr] = useState<string | null>(null);
  const [qrLoading, setQrLoading] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/config`);
        const j = await r.json();
        if (!cancelled) {
          setConfigured(Boolean(j.success && j.data?.configured));
        }
      } catch {
        if (!cancelled) setConfigured(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const onAccount = () => {
    navigate('/login', { state: location.state });
  };

  const onWeChat = async () => {
    if (!configured) {
      window.alert(
        '微信登录尚未配置。请在服务器配置 WECHAT_OPEN_APP_ID、WECHAT_OPEN_APP_SECRET、WECHAT_OPEN_REDIRECT_URI；生产环境还需配置 WECHAT_OAUTH_SUCCESS_REDIRECT（微信回调成功后跳转的前端地址）。',
      );
      return;
    }
    if (isWeChatEmbeddedBrowser()) {
      try {
        const r = await fetch(`${API_BASE_URL}/auth/wechat/start?mode=inapp`);
        const j = await r.json();
        if (j.success && j.data?.url) {
          window.location.href = j.data.url as string;
          return;
        }
        window.alert((j as { error?: string }).error || '无法发起微信授权');
      } catch {
        window.alert('网络错误，请稍后重试');
      }
      return;
    }
    setShowQrModal(true);
    setQrErr(null);
    setQrUrl(null);
    setQrLoading(true);
    try {
      const r = await fetch(`${API_BASE_URL}/auth/wechat/start?mode=qr`);
      const j = await r.json();
      if (j.success && j.data?.url) {
        setQrUrl(j.data.url as string);
      } else {
        setQrErr((j as { error?: string }).error || '无法加载二维码，请稍后重试');
      }
    } catch {
      setQrErr('网络错误，请稍后重试');
    } finally {
      setQrLoading(false);
    }
  };

  const goBack = useCallback(() => {
    navigate(getSignInReturnTo(location.state), { replace: true });
  }, [navigate, location.state]);

  return (
    <AppShell centerBody>
      <div className="login-box">
        <div className="login-flow-back-row">
          <button type="button" className="login-method-back login-method-back--top" onClick={goBack}>
            <ChevronLeft size={18} strokeWidth={2.25} aria-hidden />
            <span>返回</span>
          </button>
        </div>
        <div className="login-title-brand">luludomi</div>
        <h2>选择登录方式</h2>
        <div className="login-method-actions">
          <button type="button" className="submit-button" onClick={onAccount}>
            账号登录
          </button>
          <button
            type="button"
            className="submit-button login-method-btn-wechat"
            onClick={onWeChat}
            disabled={configured === null}
          >
            微信登录
          </button>
        </div>
      </div>
      {showQrModal ? (
        <div className="wechat-qr-modal-mask" role="presentation" onClick={() => setShowQrModal(false)}>
          <div className="wechat-qr-modal" role="dialog" aria-modal="true" onClick={(e) => e.stopPropagation()}>
            <div className="wechat-qr-modal-header">
              <h3>微信扫码登录</h3>
              <button type="button" className="wechat-qr-modal-close" onClick={() => setShowQrModal(false)}>
                关闭
              </button>
            </div>
            <p className="wechat-qr-modal-tip">请使用微信扫描下方二维码完成登录</p>
            {qrLoading ? (
              <p className="wechat-done-msg">加载中…</p>
            ) : qrErr ? (
              <p className="wechat-done-msg wechat-done-err">{qrErr}</p>
            ) : qrUrl ? (
              <>
                <iframe title="微信登录" className="wechat-qr-frame" src={qrUrl} />
                <div className="wechat-qr-fallback">
                  <button type="button" onClick={() => window.open(qrUrl, '_blank', 'noopener,noreferrer')}>
                    若无法显示，点击在新窗口打开
                  </button>
                </div>
              </>
            ) : (
              <p className="wechat-done-msg wechat-done-err">二维码地址为空，请稍后重试</p>
            )}
          </div>
        </div>
      ) : null}
    </AppShell>
  );
};

export default LoginMethod;
